import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import {
	Ets1SessionColumnVM,
} from "src/app/cbta-web/ets1/session/ets1-session-layout.utils";
import {
	buildEts1SessionReadOnlyFormGroups,
	extractEts1SessionConfigurationsFromSelection,
} from "src/app/cbta-web/ets1/session/ets1-session-selection.utils";
import { CandidateSessionConfiguration } from "src/app/models/models";
import { CONSTANT } from "src/app/constant";
import { buildPreviewSummaryTitle, buildSummaryReportTitle } from "src/app/shared/utils/form-type-label";
import { CandidateRole } from "src/app/models/enum";
import { ManeuverOutcomeDraft, OBComments, TemCompetencyOutcomeDraft } from "src/app/models/models";
import { SharedService } from "src/app/shared/services/shared.service";
import { ContainerRatingsService } from "../../../services/base/container-ratings.services";
import { getSafeCandidateRating } from "../../utils/ts1-rating-ob-interaction";
import { Ts1ReviewCompetencyBlock, Ts1ReviewManeuverRow } from "../../utils/ts1-rating-review.utils";
import {
	buildTs1PreviewCommentGroups,
	isTs1InstructorCommentVisible,
	previewCommentNaSuffix,
	previewCommentObTagClass,
	previewCommentOutcomeRateClass,
	previewCommentOutcomeRemarks,
	previewCommentPrimaryTagLabel,
	previewCommentRoleLabel,
} from "../ts1-preview-summary-comments.utils";
import { Ts1PreviewCandidateDetails } from "../ts1-preview-summary-details.utils";

@Component({
	selector: "app-ts1-rating-preview-summary",
	templateUrl: "./ts1-rating-preview-summary.component.html",
	styleUrls: ["./ts1-rating-preview-summary.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class Ts1RatingPreviewSummaryComponent implements OnInit, OnChanges {
	constructor(
		private containerRatingsService: ContainerRatingsService,
		private sharedService: SharedService,
		private fb: UntypedFormBuilder,
	) {}

	@Input() candidateNames: string[] = [];
	@Input() candidateCrewRoles: CandidateRole[] = [CandidateRole.PF, CandidateRole.PM];
	@Input() candidateIds: string[] = [];
	@Input() maneuverOutcomes: ManeuverOutcomeDraft[] = [];
	@Input() temCompetencyOutcomes: TemCompetencyOutcomeDraft[] = [];
	@Input() signOffForm: UntypedFormArray | null = null;
	@Input() competencyBlocksForIndex: (index: number) => Ts1ReviewCompetencyBlock[] = () => [];
	@Input() maneuverRowsForIndex: (index: number) => Ts1ReviewManeuverRow[] = () => [];
	@Input() summaryCommentsForIndex: (index: number) => OBComments[] = () => [];
	@Input() competencyObCommentsForIndex: (index: number) => OBComments[] = () => [];
	@Input() traineeAssessmentIdForIndex: (index: number) => string = () => "";
	@Input() candidateDetailsForIndex: (index: number) => Ts1PreviewCandidateDetails | null = () => null;
	@Input() reminderNotesEnabled = false;
	@Input() useStructuredReminderNotes = false;
	@Input() initialCandidateIndex = 0;
	@Input() hideCandidateNav = false;
	@Input() hideFooter = false;
	@Input() checklistFooter = false;
	/** When true, content flows in the page scroll instead of a nested flex viewport. */
	@Input() embedded = false;

	@Output() close = new EventEmitter<boolean>();
	@Output() next = new EventEmitter<void>();

	readonly previewCommentObTagClass = previewCommentObTagClass;
	readonly previewCommentOutcomeRemarks = previewCommentOutcomeRemarks;
	readonly previewCommentOutcomeRateClass = previewCommentOutcomeRateClass;
	readonly previewCommentRoleLabel = previewCommentRoleLabel;
	readonly previewCommentNaSuffix = previewCommentNaSuffix;
	readonly previewCommentPrimaryTagLabel = (comment: OBComments) =>
		previewCommentPrimaryTagLabel(comment, this.resetManeuvers);
	readonly hasInstructorComment = isTs1InstructorCommentVisible;

	get previewSummaryTitle(): string {
		if (this.hideFooter) {
			return buildSummaryReportTitle(CONSTANT.FORM_TYPE.ts_1);
		}
		return buildPreviewSummaryTitle(CONSTANT.FORM_TYPE.ts_1);
	}

	currentCandidateIndex = 0;
	commentsExpanded = false;
	ets1SessionColumns: Ets1SessionColumnVM[] = [];
	ets1SessionsGroup: UntypedFormGroup | null = null;
	ets1SessionRemarksGroup: UntypedFormGroup | null = null;

	ngOnInit(): void {
		this.syncCandidateIndex();
		this.rebuildEts1SessionPreview();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["initialCandidateIndex"] || changes["candidateNames"]) {
			this.syncCandidateIndex();
		}
		if (
			changes["initialCandidateIndex"] ||
			changes["candidateNames"] ||
			changes["candidateDetailsForIndex"]
		) {
			this.rebuildEts1SessionPreview();
		}
	}

	get showEts1SessionGrid(): boolean {
		return (
			!this.embedded &&
			!!this.candidateDetails?.ets1SessionCatalog?.length &&
			!!this.ets1SessionsGroup &&
			!!this.ets1SessionRemarksGroup &&
			this.ets1SessionColumns.length > 0
		);
	}

	get ets1SelectedSessionConfigurations(): CandidateSessionConfiguration[] {
		const details = this.candidateDetails;
		if (!details) return [];
		if (details.ets1SessionCatalog?.length && (details.ets1SelectedSessionCodes?.length ?? 0) > 0) {
			return extractEts1SessionConfigurationsFromSelection(
				details.ets1SessionCatalog,
				details.ets1SelectedSessionCodes ?? [],
				details.ets1SessionRemarksByCode ?? {},
			);
		}
		return details.sessionConfigurations ?? [];
	}

	get ets1SessionGroupCode(): string {
		return this.candidateDetails?.ets1SessionGroupCode ?? "ts_1";
	}

	private rebuildEts1SessionPreview(): void {
		const details = this.candidateDetails;
		if (!details?.ets1SessionCatalog?.length) {
			this.ets1SessionColumns = [];
			this.ets1SessionsGroup = null;
			this.ets1SessionRemarksGroup = null;
			return;
		}

		const built = buildEts1SessionReadOnlyFormGroups(
			this.fb,
			details.ets1SessionCatalog,
			details.ets1SelectedSessionCodes ?? [],
			details.ets1SessionRemarksByCode ?? {},
		);
		this.ets1SessionColumns = built.columns;
		this.ets1SessionsGroup = built.sessions;
		this.ets1SessionRemarksGroup = built.sessionRemarks;
	}

	/** Single-candidate report previews only populate index 0 even when the viewed trainee is candidate 2. */
	private syncCandidateIndex(): void {
		const maxIndex = Math.max(0, this.candidateNames.length - 1);
		this.currentCandidateIndex = Math.min(this.initialCandidateIndex, maxIndex);
	}

	get currentName(): string {
		return this.candidateNames[this.currentCandidateIndex] ?? "";
	}

	get candidateDetails(): Ts1PreviewCandidateDetails | null {
		return this.candidateDetailsForIndex(this.currentCandidateIndex);
	}

	get competencyBlocks(): Ts1ReviewCompetencyBlock[] {
		return this.competencyBlocksForIndex(this.currentCandidateIndex);
	}

	get maneuverRows(): Ts1ReviewManeuverRow[] {
		return this.maneuverRowsForIndex(this.currentCandidateIndex);
	}

	get summaryComments(): OBComments[] {
		return this.summaryCommentsForIndex(this.currentCandidateIndex);
	}

	get previewCommentGroups() {
		const addRemoveOb = this.embedded ? [] : getSafeCandidateRating(this.containerRatingsService.draft).addRemoveOb;
		const obList = this.sharedService.generateSortedOBList(CONSTANT.FORM_TYPE.ts_1) ?? [];
		return buildTs1PreviewCommentGroups(this.summaryComments, this.maneuverRows, {
			candidateId: this.candidateIds[this.currentCandidateIndex],
			candidateRole: this.candidateCrewRoles[this.currentCandidateIndex],
			addRemoveOb,
			obList,
			preserveCommentOrder: this.embedded,
		});
	}

	private get resetManeuvers() {
		if (this.embedded) return [];
		return getSafeCandidateRating(this.containerRatingsService.draft).resetManeuvers ?? [];
	}

	get competencyObComments(): OBComments[] {
		return this.competencyObCommentsForIndex(this.currentCandidateIndex);
	}

	get traineeAssessmentId(): string {
		return this.traineeAssessmentIdForIndex(this.currentCandidateIndex);
	}

	get signOffControl(): UntypedFormGroup | null {
		const ctrl = this.signOffForm?.at(this.currentCandidateIndex) as UntypedFormGroup | undefined;
		return ctrl ?? null;
	}

	get generalObservationText(): string {
		return String(this.signOffControl?.get("generalObservation")?.value ?? "").trim();
	}

	get reminderNoteResponses(): UntypedFormArray | null {
		const control = this.signOffControl?.get("reminderNoteResponses");
		return control instanceof UntypedFormArray ? control : null;
	}

	get reminderNotesText(): string {
		return String(this.signOffControl?.get("reminderNotes")?.value ?? "").trim();
	}

	prevCandidate(): void {
		if (this.currentCandidateIndex > 0) {
			this.currentCandidateIndex--;
		}
	}

	nextCandidate(): void {
		if (this.currentCandidateIndex < this.candidateNames.length - 1) {
			this.currentCandidateIndex++;
		}
	}

	onBack(): void {
		this.close.emit(false);
	}

	onClose(): void {
		this.close.emit(true);
	}

	onNext(): void {
		this.next.emit();
	}

	toggleComments(): void {
		this.commentsExpanded = !this.commentsExpanded;
	}
}
